import React, { useCallback, useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import {
  Avatar,
  Skeleton,
  Box,
  Card,
  CardContent,
  IconButton,
  Button
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { pdf } from '@react-pdf/renderer';
import saveAs from 'file-saver';

import Table from '../../components/table';
import customerService from '../../services/customerService';
import { useAuth } from '../../contexts/auth';
import { phoneNumberNoDDI, toBrazilDateTime } from '../../components/mask';
import subsidiaryService from '../../services/subsidiaryService';
import ExportCSV from '../../components/exportExcel';
import CustomersPrint from './components/customersPrint';
import CustomerDetails from './details';
import CustomerEdit from './edit';

interface OwnProps {
  subsidiaryId: string;
  title: string;
}

interface CustomerRow {
  Nome: string;
  Email: string;
  Telefone: string;
  'Data Cadastro': string;
  Franquia: string;
  Habilitado: string;
}

export default function page(props: OwnProps) {
  const [total, setTotal] = React.useState<number>(0);
  const [items, setItems] = React.useState<any>([]);
  const [enableSearch, setEnableSearch] = React.useState<boolean>(false);

  let { subsidiaryId } = useAuth();
  if (props.subsidiaryId) subsidiaryId = props.subsidiaryId;
  const subsidiaryFilter = subsidiaryId ? `subsidiary:${subsidiaryId}` : null;

  const [itemsPag, setItemsPag] = React.useState<any>([]);
  const [current, setCurrent] = React.useState<number>(1);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentTotal, setCurrentTotal] = React.useState<number>(0);
  const [isStart, setIsStart] = React.useState<boolean | undefined>();
  const [isEnd, setIsEnd] = React.useState<boolean | undefined>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  // Estados para controle dos modais
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [modalType, setModalType] = useState<'details' | 'edit' | null>(null);

  // Buscar todos os clientes para exportação
  const getAllCustomers = async () => {
    try {
      const subsidiaries = await subsidiaryService.getCities();
      const resp = await customerService.search('', 1000, 0, subsidiaryFilter, subsidiaries);
      return resp.items || [];
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      return [];
    }
  };

  // Converter dados para o formato do PDF/Excel
  const convertToExportData = (customers: any[]): CustomerRow[] => customers.map((customer) => ({
    Nome: customer.name || '',
    Email: customer.email || '',
    Telefone: phoneNumberNoDDI(customer.phoneNumber) || 'Não informado',
    'Data Cadastro': customer.createdAt ? toBrazilDateTime(customer.createdAt) : '',
    Franquia: customer.subsidiaryName || '',
    Habilitado: customer.disabledAt ? 'Inativo' : 'Ativo'
  }));

  // Gerar PDF
  const generatePDF = async () => {
    try {
      const customers = await getAllCustomers();
      const exportData = convertToExportData(customers);

      const pdfDocument = (
        <CustomersPrint
          data={exportData}
          fileName={format(new Date(), 'yyyyMMdd_hhmmss')}
        />
      );

      const blob = await pdf(pdfDocument).toBlob();
      saveAs(blob, `clientes_${format(new Date(), 'yyyyMMdd_hhmmss')}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  const searchCustomers = async (userSearching: string, limit: number, filters: string | null = null, subsidiaries: any | null) => {
    const params = userSearching?.length ? userSearching : '';

    // ✅ Ajuste para paginação 0-based no service (backend espera 0-based)
    const pageForService = currentPage > 0 ? currentPage - 1 : 0;

    const resp = await customerService.search(params, limit, pageForService, filters, subsidiaries);
    return resp;
  };

  const getPrev = async () => {
    if (!isStart && currentPage > 1) { // ✅ Verifica se currentPage > 1
      const newCurrentPage = currentPage - 1;
      setCurrentPage(newCurrentPage);
      await setList(subsidiaryFilter, 20, null);
    }
  };

  const getNext = async () => {
    if (!isEnd) {
      const newCurrentPage = currentPage + 1;
      setCurrentPage(newCurrentPage);
      await setList(subsidiaryFilter, 20, null);
    }
  };

  const getList = async (filters: string | null = null, limitPerPage: number, subsidiaries: any) => {
    try {
      const resp: any = await searchCustomers('', limitPerPage, filters, subsidiaries);
      const customers = resp.items;
      setIsLoading(false);
      return {
        pagination: resp,
        customers
      };
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      return {
        pagination: {},
        customers: []
      };
    }
  };

  const setList = async (filters: string | null = null, limitPerPage: number, subsidiaries: any) => {
    try {
      const subs = subsidiaries ?? null;
      const resp = await getList(filters, limitPerPage, subs);
      if (resp?.pagination) {
        // ✅ Cálculo correto para current e currentTotal (1-based)
        const startItem = ((resp.pagination.currentPage) * resp.pagination.hitsPerPage) + 1;
        const endItem = Math.min(startItem + resp.pagination.hitsPerPage - 1, resp.pagination.totalHits);
        setCurrent(startItem);
        setCurrentTotal(endItem);
        setIsStart(resp.pagination.isStart);
        setIsEnd(resp.pagination.isEnd);
        setTotal(resp.pagination.totalHits);
        setItemsPag(resp.customers);
      }
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    subsidiaryService.getCities().then(async (subsidiaries: any) => {
      await setList(subsidiaryFilter, 20, subsidiaries);
      setIsLoading(false);
    });
  }, [currentPage]);

  const searchItem = useCallback(async (params: string) => {
    setIsLoading(true);
    if (params?.length < 4) return;
    const subsidiaries = await subsidiaryService.getCities();
    searchCustomers(params, 300, subsidiaryFilter, subsidiaries).then(async (resp: any) => {
      const customers = resp.items;
      setItems(customers);
      setTotal(resp.totalHits);
      setEnableSearch(true);
      setIsLoading(false);
    });
  }, [currentPage]);

  const columns = [
    {
      name: 'id',
      options: { display: false }
    },
    {
      name: 'photoURL',
      options: { display: false }
    },
    {
      name: 'address',
      options: { display: false }
    },
    {
      name: 'email',
      options: { display: false }
    },
    {
      name: 'name',
      label: 'Nome',
      options: {
        customBodyRender: (value: string, tableMeta: any) => {
          if (value) {
            return (
              <div className="d-flex align-items-center">
                <Avatar alt="" className="mr-2" src={tableMeta.rowData[1]}>
                  {value.substr(0, 1)}
                </Avatar>
                <div>
                  {ReactHtmlParser(value)}
                  {/* <span className="text-black-50 d-block">
                    {ReactHtmlParser(tableMeta.rowData[2]) }
                  </span> */}
                  <span className="text-black-50 d-block">
                    {ReactHtmlParser(subsidiaryId === undefined ? tableMeta.rowData[7] : null)}
                  </span>
                </div>
              </div>
            );
          }
          return <Skeleton />;
        }
      }
    },
    {
      name: 'phoneNumber',
      label: 'Celular/Email',
      options: {
        filter: false,
        customBodyRender: (value: string, tableMeta: any) => {
          if (value) {
            return (
              <div>
                {ReactHtmlParser(phoneNumberNoDDI(value))}
                <span className="text-black-50 d-block">
                  {ReactHtmlParser(tableMeta.rowData[3])}
                </span>
              </div>
            );
          }
          return (
            <div className="text-dark">Celular não cadastrado</div>
          );
        }
      }
    },
    {
      name: 'createdAt',
      label: 'Data de cadastro',
      options: {
        customBodyRender: (value: Date) => {
          if (value) {
            return (
              <div className="d-flex align-items-center">
                {value.toLocaleString('pt-BR')}
              </div>
            );
          }
          return <Skeleton />;
        }
      }
    },
    {
      name: 'subsidiaryName',
      label: 'Franquia',
      options: { display: false, filter: false }
    },
    {
      name: 'disabledAt',
      label: 'Habilitado',
      options: {
        filter: false,
        customBodyRender: (value: Date | null) => (
          <span className={`btn-pill m-1 badge badge-${value ? 'danger' : 'success'}`}>
            {value ? 'Inativo' : 'Ativo'}
          </span>
        )
      }
    }
  ];

  const onRowClick = (rowData: string[]) => {
    const id = rowData[0];
    if (id) {
      setSelectedCustomer(id);
      setModalType('details');
    }
  };

  const handleCreate = () => {
    setSelectedCustomer(null);
    setModalType('edit');
  };

  const handleCloseModal = () => {
    setSelectedCustomer(null);
    setModalType(null);
  };

  const handleSave = () => {
    setIsLoading(true);
    subsidiaryService.getCities().then(async (subsidiaries: any) => {
      await setList(subsidiaryFilter, 20, subsidiaries);
      setSelectedCustomer(null);
      setModalType(null);
    });
  };

  const handleEdit = () => {
    setModalType('edit');
  };

  const onTableChange = useCallback((action: string, state: any) => {
    if (action === 'search') {
      if (state.searchText !== null) {
        searchItem(state.searchText);
      }
    }

    if (action === 'onSearchClose') {
      setEnableSearch(false);
      setCurrentPage(1);
      subsidiaryService.getCities().then(async (subsidiaries: any) => {
        await setList(subsidiaryFilter, 20, subsidiaries);
        setIsLoading(false);
      });
    }
  }, [currentPage]);

  // Dados para exportação Excel
  const getRowsToReport = () => {
    const customers = enableSearch ? items : itemsPag;
    return convertToExportData(customers);
  };

  return (
    <>
      <Card className="card-box mb-4">
        <div className="card-header">
          <div className="card-header--title" />
          <Box className="card-header--actions">
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreate}
              className="mr-2"
            >
              Novo Cliente
            </Button>
            <IconButton
              size="small"
              color="primary"
              className="text-primary"
              title="Exportar para PDF"
              onClick={generatePDF}
            >
              <FontAwesomeIcon icon={['far', 'file-pdf']} className="font-size-lg" />
            </IconButton>
            {' | '}
            <ExportCSV csvData={getRowsToReport()} fileName={format(new Date(), 'yyyyMMdd_hhmmss')} />
          </Box>
        </div>
        <CardContent className="p-0">
          <Table
            title={props.title ?? 'Clientes'}
            createUrl={undefined}
            data={enableSearch ? items : itemsPag}
            columns={columns}
            filter={false}
            serverSide
            isLoading={isLoading}
            onRowClick={onRowClick}
            onTableChange={onTableChange}
            pagination={false}
            getPrev={getPrev}
            getNext={getNext}
            current={current}
            currentTotal={currentTotal}
            isStart={isStart}
            isEnd={isEnd}
            enableSearch={enableSearch}
            count={total}
          />
        </CardContent>
      </Card>

      {/* Modais */}
      {modalType === 'details' && selectedCustomer && (
        <CustomerDetails
          open
          customerId={selectedCustomer}
          onClose={handleCloseModal}
          onEdit={handleEdit}
        />
      )}

      {modalType === 'edit' && (
        <CustomerEdit
          open
          customerId={selectedCustomer}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </>
  );
}
